import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchCaseStudies } from "@/lib/api";
import BrandMarquee from "@/components/BrandMarquee";
import { TIDS } from "@/constants/testIds";

export default function Work() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchCaseStudies()
      .then(setItems)
      .catch(() => {});
  }, []);

  const filtered = items.filter((c) =>
    filter === "all" ? true : c.region === filter
  );

  return (
    <div className="relative">
      <section className="pt-36 pb-16 grain relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#1D2AE8]/80 font-semibold">
            Selected Work · 2016 → Now
          </p>
          <h1 className="font-display leading-[0.92] mt-6" style={{ fontSize: "clamp(3rem, 10vw, 8.5rem)" }}>
            The brands we've
            <br />
            <em>illustrated.</em>
          </h1>

          <div className="mt-10 flex gap-3">
            {[
              { k: "all", label: "All" },
              { k: "india", label: "India" },
              { k: "global", label: "Global" },
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`work-filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                  filter === f.k
                    ? "bg-[#0B0B0B] text-[#FFC629]"
                    : "bg-white/20 text-[#0B0B0B] hover:bg-white/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="py-20">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {filtered.map((c, i) => (
              <motion.article
                key={c.slug}
                data-testid={TIDS.card(c.slug)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
                className={`case-card glass-light rounded-3xl overflow-hidden ${
                  i % 5 === 0
                    ? "md:col-span-4"
                    : i % 5 === 3
                      ? "md:col-span-4"
                      : "md:col-span-2"
                }`}
              >
                <div className="img-wrap aspect-[4/3] rounded-none">
                  <img src={c.image} alt={c.name} loading="lazy" />
                </div>
                <div className="p-6 flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.25em] text-[#1D2AE8]/60">
                        {c.category}
                      </p>
                      <p className="font-display text-3xl mt-2">{c.name}</p>
                    </div>
                    <span className="bg-[#0B0B0B] text-[#FFC629] rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap">
                      {c.metric}
                    </span>
                  </div>
                  <p className="text-sm text-[#1D2AE8]/75 leading-relaxed">
                    {c.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-[#1D2AE8]/60 pt-3 border-t border-[#0B0B0B]/10">
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} /> {c.country}
                    </span>
                    <span className="flex flex-wrap gap-2 justify-end">
                      {c.services?.slice(0, 3).map((s) => (
                        <span
                          key={s}
                          className="bg-white/40 rounded-full px-2 py-0.5"
                        >
                          {s}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 text-center">
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.95]">
            Yours could be
            <br />
            <em>next</em>.
          </h2>
          <Link to="/contact" className="btn-ink mt-8 inline-flex">
            Pitch us your brand <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
